import * as React from 'react';
import { Link } from 'react-router';
import { cn } from '../lib/cn';
import { navigationPatterns } from '../design-system/patterns/navigation';

export interface BreadcrumbItem {
  label: string;
  /** When omitted, the item renders as plain text (the "current" page). */
  href?: string;
}

export interface UniversalPageHeaderProps {
  /** Page title — string or any node (e.g. icon + text). */
  title: React.ReactNode;
  /** Short descriptive line under the title — keep to one sentence. */
  description?: string;
  breadcrumbs?: BreadcrumbItem[];
  /** Back ("up") target.
   *
   *  Omit it and the parent is DERIVED from `breadcrumbs` — the second-to-last
   *  entry, when it carries an href. 58 of the portal's 64 pages already declare
   *  a trail, so they gain the control without being edited: don't declare what
   *  you can derive.
   *
   *  Pass `null` to suppress it on a page that genuinely has no parent (a
   *  dashboard, a top-level list).
   *
   *  This is deliberately an UP link, not a browser-history back. `navigate(-1)`
   *  is non-deterministic: a reader who arrived from an emailed alert, a
   *  bookmark or a dashboard action-queue row would be thrown clean out of the
   *  app by it. An up link goes to the same place every time, which is the only
   *  behaviour that can be labelled truthfully. */
  backTo?: { label: string; href: string } | null;
  /** Right-aligned actions — typically primary + secondary buttons. */
  actions?: React.ReactNode;
  /** Optional metadata strip rendered below the title row (e.g. status, IDs). */
  meta?: React.ReactNode;
  className?: string;
}

/**
 * UniversalPageHeader — the single source of page chrome for all portal pages.
 *
 * Architecture rules (ARCHITECTURE_FRAMEWORK.md §4.4):
 * - Every portal page renders this exactly once.
 * - Loading and error states render the same header so chrome stays stable.
 * - Do not build custom page headers.
 */
export function UniversalPageHeader({
  title,
  description,
  breadcrumbs,
  backTo,
  actions,
  meta,
  className,
}: UniversalPageHeaderProps) {
  const back = resolveBackTo(backTo, breadcrumbs);
  return (
    <header className={cn('space-y-2 pb-6', className)}>
      {/* Below `sm` the trail is HIDDEN and the up link stands alone — a
          multi-level breadcrumb wraps badly on a phone. From `sm` up both
          show: the trail states where you are, the up link is the control you
          press. That is one line of redundancy and it is deliberate; a
          muted-grey trail does not read as a button, which is exactly the
          complaint this closes. */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <div className={back ? 'hidden sm:block' : undefined}>
          <Breadcrumbs items={breadcrumbs} />
        </div>
      )}

      {back && (
        <Link
          to={back.href}
          className="inline-flex items-center gap-1.5 rounded-sm text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <span aria-hidden="true">&larr;</span>
          {back.label}
        </Link>
      )}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1 space-y-1">
          <h1 className="font-display text-2xl font-semibold leading-tight tracking-tight text-foreground">
            {title}
          </h1>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>

        {actions && (
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        )}
      </div>

      {meta && <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">{meta}</div>}
    </header>
  );
}

/** Explicit target wins; `null` suppresses; otherwise derive the parent from
 *  the trail. A trail of one is a top-level page and yields nothing, and a
 *  parent without an href is a label rather than a destination. */
function resolveBackTo(
  backTo: { label: string; href: string } | null | undefined,
  breadcrumbs: BreadcrumbItem[] | undefined,
): { label: string; href: string } | null {
  if (backTo !== undefined) return backTo;
  if (!breadcrumbs || breadcrumbs.length < 2) return null;
  const parent = breadcrumbs[breadcrumbs.length - 2];
  if (!parent?.href) return null;
  return { label: parent.label, href: parent.href };
}

function Breadcrumbs({ items }: { items: BreadcrumbItem[] }) {
  return (
    <nav
      aria-label="Breadcrumb"
      className={navigationPatterns.breadcrumb.wrapper}
    >
      <ol className="flex items-center gap-1.5">
        {items.map((item, idx) => {
          const isLast = idx === items.length - 1;
          return (
            <li key={`${item.label}-${idx}`} className="flex items-center gap-1.5">
              {idx > 0 && (
                <span aria-hidden="true" className={navigationPatterns.breadcrumb.separator}>
                  /
                </span>
              )}
              {item.href && !isLast ? (
                <Link
                  to={item.href}
                  className="hover:text-foreground hover:underline"
                >
                  {item.label}
                </Link>
              ) : (
                <span
                  className={isLast ? navigationPatterns.breadcrumb.current : undefined}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {item.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
